import React, { useState } from 'react';
import apiConfig from '../config/apiConfig';
import { previewAnimesCsv, importAnimesCsv } from '../services/animeService';

const ImportAnimeModal = ({ isOpen, onClose }) => {
  console.log("ImportAnimeModal renderizado con isOpen:", isOpen);
  
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importStep, setImportStep] = useState(1); // 1: Selección, 2: Vista previa, 3: Confirmación
  const [importResults, setImportResults] = useState(null);
  const [parsedData, setParsedData] = useState(null);

  // No usamos Redux por ahora

  // Usar la misma configuración de API_BASE_URL que en otros servicios
  const API_URL = apiConfig.base || process.env.REACT_APP_API_URL || "http://localhost:4000";
  
  const getToken = () => {
    return localStorage.getItem("token");
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && selectedFile.name.split('.').pop().toLowerCase() !== 'csv') {
        setError('Por favor selecciona un archivo CSV válido');
        setFile(null);
        setFileName('');
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError('');
    }
  };

  const handlePreviewFile = async () => {
    if (!file) {
      setError('Por favor selecciona un archivo CSV');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await previewAnimesCsv(file);

      if (response.success) {
        setPreviewData(response);
        setImportStep(2); // Avanzar a la vista previa
      } else {
        setError(response.message || 'Error al procesar el archivo');
      }
    } catch (error) {
      console.error('Error previsualizando CSV:', error);
      setError(error.response?.data?.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleImportAnimes = async () => {
    if (!previewData || !previewData.data || previewData.data.length === 0) {
      setError('No hay animes para importar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await importAnimesCsv(previewData.data);

      if (response.success) {
        setImportResults(response);
        setParsedData(response.parsedData);
        setImportStep(3); // Avanzar a la confirmación
        // La actualización de la lista se manejará desde AnimesListAdmin con refreshAnimesList
      } else {
        setError(response.message || 'Error al importar los animes');
      }
    } catch (error) {
      console.error('Error importando animes:', error);
      setError(error.response?.data?.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setFile(null);
    setFileName('');
    setPreviewData(null);
    setError('');
    setImportStep(1);
    setImportResults(null);
    setParsedData(null);
  };

  const handleClose = (options = {}) => {
    resetModal();
    onClose(options);
  };

  if (!isOpen) {
    return null;
  }

  // Función para contar temporadas en un anime
  const countSeasons = (anime) => {
    return anime.seasons ? anime.seasons.length : 0;
  };

  // Función para contar episodios en un anime
  const countEpisodes = (anime) => {
    if (!anime.seasons) return 0;
    return anime.seasons.reduce((count, season) => 
      count + (season.episodes ? season.episodes.length : 0), 0);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[9999]" 
      style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
    >
      <div className="bg-gray-900 border border-gray-700 p-6 rounded-xl shadow-xl w-11/12 max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold text-white">Importación Jerárquica Completa de Animes</h2>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-gray-800"
            aria-label="Cerrar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-900/50 border border-red-500 text-white p-4 mb-6 rounded-lg">
            <p>{error}</p>
          </div>
        )}

        {importStep === 1 && (
          <>
            <div className="mb-6">
              <p className="text-gray-300 mb-4">
                Esta función permite importar animes completos con sus temporadas y episodios desde un único archivo CSV.
                Ideal para cargar nuevos animes o actualizar varios animes a la vez.
              </p>
              <p className="text-gray-400 text-sm mb-2">Estructura del archivo CSV:</p>
              <div className="bg-gray-800 p-3 rounded-md mb-4 overflow-x-auto">
                <p className="text-xs text-gray-300 mb-2">El archivo debe tener una estructura jerárquica con los siguientes tipos de filas:</p>
                
                <h4 className="text-xs text-white font-bold mt-3">1. Filas tipo "anime" (obligatorio)</h4>
                <ul className="list-disc pl-5 text-gray-400 text-xs mb-3">
                  <li><b>tipo</b>: "anime" (obligatorio)</li>
                  <li><b>id_referencia</b>: identificador único para vincular con temporadas (obligatorio)</li>
                  <li><b>name</b>: nombre del anime (obligatorio)</li>
                  <li><b>description</b>: descripción del anime (opcional)</li>
                  <li><b>genre</b>: género(s) del anime (obligatorio)</li>
                  <li><b>poster</b>: URL de la imagen de portada (obligatorio)</li>
                  <li><b>banner</b>: URL de la imagen de banner (opcional)</li>
                  <li><b>releaseYear</b>: año de lanzamiento (opcional, por defecto el año actual)</li>
                </ul>
                
                <h4 className="text-xs text-white font-bold mt-3">2. Filas tipo "temporada" o "season" (obligatorio)</h4>
                <ul className="list-disc pl-5 text-gray-400 text-xs mb-3">
                  <li><b>tipo</b>: "temporada" o "season" (obligatorio)</li>
                  <li><b>id_referencia</b>: debe coincidir con el id_referencia del anime padre (obligatorio)</li>
                  <li><b>seasonNumber</b>: número de temporada (obligatorio)</li>
                  <li><b>title</b>: título de la temporada (opcional, por defecto "Temporada X")</li>
                  <li><b>description</b>: descripción de la temporada (opcional)</li>
                  <li><b>poster</b>: URL de la imagen de portada (opcional, usa el del anime por defecto)</li>
                  <li><b>releaseDate</b>: fecha de lanzamiento (opcional, formato YYYY-MM-DD)</li>
                </ul>
                
                <h4 className="text-xs text-white font-bold mt-3">3. Filas tipo "episodio" o "episode" (obligatorio)</h4>
                <ul className="list-disc pl-5 text-gray-400 text-xs mb-3">
                  <li><b>tipo</b>: "episodio" o "episode" (obligatorio)</li>
                  <li><b>id_referencia</b>: debe coincidir con id_referencia del anime + "S" + seasonNumber (obligatorio)</li>
                  <li><b>episodeNumber</b>: número de episodio (obligatorio)</li>
                  <li><b>name</b>: nombre del episodio (obligatorio)</li>
                  <li><b>description</b>: descripción del episodio (opcional)</li>
                  <li><b>runtime</b>: duración en minutos (obligatorio)</li>
                  <li><b>videoUrl</b>: URL del video (obligatorio)</li>
                  <li><b>srtUrl</b>: URL de los subtítulos (obligatorio)</li>
                  <li><b>fileSize</b>: tamaño del archivo en bytes (obligatorio)</li>
                  <li><b>mimeType</b>: tipo MIME del video (obligatorio, ej: "video/mp4")</li>
                </ul>
                
                <h4 className="text-xs text-white font-bold mt-3">Ejemplo:</h4>
                <code className="text-xs text-green-300 block mt-1">
                  tipo,id_referencia,name,genre,poster,banner,releaseYear,seasonNumber,title,episodeNumber,runtime,videoUrl,fileSize,mimeType,srtUrl<br/>
                  anime,naruto,Naruto,Acción,http://example.com/poster.jpg,http://example.com/banner.jpg,2002,,,,,,,,<br/>
                  temporada,naruto,,,,,2002,1,Primera Temporada,,,,,,,<br/>
                  episodio,narutoS1,,,,,,,,1,22,http://example.com/ep1.mp4,104857600,video/mp4,http://example.com/subs1.srt<br/>
                </code>
              </div>
              <button 
                onClick={() => {
                  // Crear contenido CSV para la plantilla
                  const headers = 'tipo,id_referencia,name,genre,poster,banner,releaseYear,seasonNumber,title,episodeNumber,runtime,videoUrl,fileSize,mimeType,srtUrl';
                  const animeRow = 'anime,naruto,Naruto,Acción,http://example.com/poster.jpg,http://example.com/banner.jpg,2002,,,,,,,,';
                  const seasonRow = 'temporada,naruto,,,,,2002,1,Primera Temporada,,,,,,,';
                  const episodeRow = 'episodio,narutoS1,,,,,,,,1,22,http://example.com/ep1.mp4,104857600,video/mp4,http://example.com/subs1.srt';
                  
                  // Crear blob y link para descarga
                  const csvContent = `${headers}\n${animeRow}\n${seasonRow}\n${episodeRow}`;
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  
                  // Crear elemento a para descargar
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'plantilla_animes_completa.csv');
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                Descargar plantilla CSV completa
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <label className="block">
                <span className="text-gray-300 mb-1 block">Seleccionar archivo CSV:</span>
                <div className="mt-1 flex items-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-file-input"
                  />
                  <label
                    htmlFor="csv-file-input"
                    className="cursor-pointer flex-1 bg-gray-800 border border-gray-700 hover:border-gray-500 rounded-lg p-3 text-center"
                  >
                    {fileName ? fileName : 'Click para seleccionar archivo'}
                  </label>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleClose}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg flex-1 sm:flex-none sm:px-6"
              >
                Cancelar
              </button>
              <button
                onClick={handlePreviewFile}
                disabled={!file || loading}
                className={`bg-blue-500 text-white p-2 rounded-lg flex-1 sm:flex-none sm:px-6 ${!file || loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
              >
                {loading ? 'Procesando...' : 'Vista Previa'}
              </button>
            </div>
          </>
        )}

        {importStep === 2 && previewData && (
          <>
            <div className="mb-4">
              <p className="text-green-400 mb-2">{previewData.message}</p>
              <p>Se han detectado {previewData.data.length} animes con {previewData.totalSeasons} temporadas y {previewData.totalEpisodes} episodios. Revisa que los datos sean correctos antes de importar.</p>
            </div>
            
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full bg-gray-900 rounded-lg overflow-hidden">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left">#</th>
                    <th className="px-4 py-2 text-left">Nombre</th>
                    <th className="px-4 py-2 text-left">Género</th>
                    <th className="px-4 py-2 text-left">Temporadas</th>
                    <th className="px-4 py-2 text-left">Episodios</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {previewData.data.slice(0, 10).map((anime, index) => (
                    <tr key={`preview-anime-${index}`} className="hover:bg-gray-800">
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{anime.name}</td>
                      <td className="px-4 py-2">{anime.genre}</td>
                      <td className="px-4 py-2">{countSeasons(anime)}</td>
                      <td className="px-4 py-2">{countEpisodes(anime)}</td>
                    </tr>
                  ))}
                  {previewData.data.length > 10 && (
                    <tr>
                      <td colSpan="5" className="px-4 py-2 text-center text-gray-400">
                        ... y {previewData.data.length - 10} animes más
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setImportStep(1)}
                className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg px-6"
              >
                Atrás
              </button>
              <button
                onClick={handleImportAnimes}
                disabled={loading}
                className={`bg-blue-500 text-white p-2 rounded-lg px-6 ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-600'}`}
              >
                {loading ? 'Importando...' : 'Importar Animes'}
              </button>
            </div>
          </>
        )}

        {importStep === 3 && parsedData && (
          <>
            <div className="text-center mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-green-500 mx-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <h3 className="text-xl font-bold text-white mt-4">Importación exitosa</h3>
              <p className="text-gray-400 mt-2">
                Los datos se han importado correctamente en el sistema.
              </p>
            </div>

            <div className="mb-6 border-t border-gray-700 pt-4">
              <h4 className="text-lg font-semibold text-white mb-3">Animes importados:</h4>
              <div className="space-y-4 max-h-60 overflow-y-auto">
                {importResults.animes.map((anime, index) => (
                  <div key={`anime-${index}`} className="bg-gray-700/50 p-3 rounded-md">
                    <p className="font-medium text-white">{anime.name}</p>
                    
                    {anime.seasons && anime.seasons.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-300 mb-2">Temporadas:</p>
                        <ul className="list-disc pl-5 text-sm text-gray-400 space-y-2">
                          {anime.seasons.map((season, sIndex) => (
                            <li key={`season-${sIndex}`} className="flex justify-between items-center">
                              <span>
                                Temporada {season.seasonNumber}: {season.episodeCount || 0} episodios
                              </span>
                              <button
                                onClick={() => {
                                  // Almacenar el ID de la temporada seleccionada en localStorage
                                  localStorage.setItem("selectedSeasonId", season.id);
                                  
                                  // Cerrar este modal y notificar al componente padre para redirigir
                                  handleClose({ 
                                    redirectToEpisodes: true, 
                                    seasonId: season.id,
                                    animeId: anime.id
                                  });
                                }}
                                className="text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 px-2 rounded"
                              >
                                Agregar Episodios
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => handleClose()}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-8 rounded-lg transition-colors duration-200"
              >
                Cerrar
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ImportAnimeModal;
